import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { getResizedImageUrl } from '@rolebase/shared/helpers/getResizedImageUrl'
import { Box, BoxProps, HStack, Image, Text } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

const iconHeight = 56

export default function DashboardWelcome(boxProps: BoxProps) {
  const { t } = useTranslation()
  const { org } = useOrgContext()
  const currentMember = useCurrentMember()

  if (!org) return null

  // Deliberately NOT the shared OrgIcon component: it crops to a square
  // (objectFit cover), which is right for a 20px bullet in a menu but mangles a
  // wide logo at this size. Here the org's logo is the subject, so it is shown
  // whole.
  const iconUrl = org.icon
    ? getResizedImageUrl(org.icon, iconHeight * 2) || undefined
    : undefined

  // No icon means NO reserved space: an empty slot would read as a broken
  // image, which is exactly what BUG-01 showed on this very screen.
  const icon = iconUrl ? (
    <Image
      src={iconUrl}
      alt={org.name}
      h={`${iconHeight}px`}
      maxW="180px"
      objectFit="contain"
      flexShrink={0}
    />
  ) : null

  // Two keys rather than an empty interpolation: currentMember is undefined
  // while loading, and a super-admin can have no member record in the org at
  // all (the silent case already met on GOUV-06). "Bienvenue undefined" must
  // never be possible.
  const message = currentMember?.name
    ? t('DashboardWelcome.withName', {
        memberName: currentMember.name,
        orgName: org.name,
      })
    : t('DashboardWelcome.withoutName', { orgName: org.name })

  return (
    <Box {...boxProps}>
      <HStack spacing={4} align="center">
        {icon}
        <Text fontSize="lg" color="gray.600" _dark={{ color: 'gray.300' }}>
          {message}
        </Text>
      </HStack>
    </Box>
  )
}
